import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Box,
  MenuItem,
  Stack,
  type SxProps,
  TextField,
  type Theme,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useIsMobile } from '@side/pantry';
import type { SuccessResponseJSON } from 'openapi-typescript-helpers';
import { useMemo } from 'react';
import type { IdentitySchema } from '$api-clients/generated';
import { useIdentity } from '$api-clients/hooks';

export type ViewTeam = {
  id: string;
  alias?: string | null;
  legalName?: string | null;
};

type TeamRecord = SuccessResponseJSON<
  IdentitySchema.paths['/teams']['get']
>['data'][number];

type TeamSelectOptionProps = {
  team?: TeamRecord;
  selected?: boolean;
};

const TeamSelectOption = ({ team, selected }: TeamSelectOptionProps) => {
  if (!team) return null;

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        flex: 1,
        overflow: 'hidden',
      }}
    >
      {selected && (
        <VisibilityOutlinedIcon
          sx={{ width: 16, height: 16, color: 'action.active' }}
        />
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant={selected ? 'body1' : 'body3'}
          noWrap
          sx={{
            color: 'text.primary',
            alignSelf: 'flex-start',
            maxWidth: '100%',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {team.alias}
        </Typography>
        {!selected && (
          <Typography variant="body3" sx={{ alignSelf: 'flex-start' }}>
            {team.supervisingBroker?.license.state}:{' '}
            {team.supervisingBroker?.license.number}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

type ViewTeamSelectorProps = {
  teams: (ViewTeam | undefined)[];
  teamId?: string;
  handleTeamChange: (event: SelectChangeEvent) => void;
  sx?: SxProps<Theme>;
};
export const ViewTeamSelector = ({
  teams,
  teamId,
  handleTeamChange,
  sx,
}: ViewTeamSelectorProps) => {
  const isMobile = useIsMobile('md');
  const validTeams = teams.flatMap((t) => (t ? t : []));
  const teamIds = validTeams.map((t) => t.id);

  // `/auth/userinfo` (the source for the team list) doesn't carry team status
  // or supervisingBroker, so fetch the full teams to show the license sub-line.
  // A single `id[in]` query avoids an N+1 burst of `/teams/{teamId}` requests.
  const { data } = useIdentity(
    '/teams',
    teamIds.length > 1
      ? {
          params: {
            query: {
              q: `id[in]=[${teamIds.join(',')}]`,
              limit: teamIds.length,
            },
          },
        }
      : null,
  );

  const teamsById = useMemo(() => {
    const map = new Map<string, TeamRecord>();
    for (const team of data?.data ?? []) {
      map.set(team.id, team);
    }
    return map;
  }, [data]);

  if (validTeams.length <= 1) {
    return null;
  }

  return (
    <TextField
      select
      fullWidth
      size={!isMobile ? 'small' : 'medium'}
      value={teamId || ''}
      sx={{
        ...(isMobile && {
          '& .MuiOutlinedInput-input': {
            paddingTop: '10.5px',
            paddingBottom: '10.5px',
          },
        }),
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          paddingRight: '32px !important',
        },
        ...sx,
      }}
      slotProps={{
        select: {
          'aria-label': 'Select Team',
          onChange: (event) => {
            handleTeamChange(event as SelectChangeEvent<string>);
          },
          renderValue: (selected) => (
            <TeamSelectOption
              team={teamsById.get(selected as string)}
              selected
            />
          ),
        },
      }}
    >
      {validTeams.map((team) => (
        <MenuItem key={team.id} value={team.id}>
          <TeamSelectOption team={teamsById.get(team.id)} />
        </MenuItem>
      ))}
    </TextField>
  );
};
